import { useState, useCallback } from 'react';
import { metaApi } from '../services/api';
import toast from 'react-hot-toast';

/**
 * Hook that encapsulates all Meta Ads API interactions.
 */
const useMeta = () => {
  const [accounts, setAccounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [insights, setInsights] = useState([]);
  const [pages, setPages] = useState([]);
  const [leadForms, setLeadForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const withLoading = async (fn) => {
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err.message || 'Something went wrong');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Connect Meta via OAuth
  const connectMeta = useCallback(async () => {
    return withLoading(async () => {
      const response = await metaApi.getAuthUrl();
      window.location.href = response.data.authUrl;
    });
  }, []);

  // Fetch user's ad accounts from DB (cached from previous sync)
  const fetchAccounts = useCallback(async () => {
    return withLoading(async () => {
      const response = await metaApi.getAccounts();
      const accs = response.data?.accounts || [];
      setAccounts(accs);
      return accs;
    });
  }, []);

  // Sync accounts fresh from Meta API
  const syncAccounts = useCallback(async () => {
    return withLoading(async () => {
      const response = await metaApi.syncAccounts();
      toast.success('Accounts synced successfully!');
      // Re-fetch after sync
      const refreshed = await metaApi.getAccounts();
      const accs = refreshed.data?.accounts || [];
      setAccounts(accs);
      return accs;
    });
  }, []);

  // Fetch campaigns for an account
  const fetchCampaigns = useCallback(async (accountId) => {
    return withLoading(async () => {
      const response = await metaApi.getCampaigns(accountId);
      const camps = response.data?.campaigns || [];
      setCampaigns(camps);
      return camps;
    });
  }, []);

  // Fetch insights for an account
  const fetchInsights = useCallback(async (accountId, params = {}) => {
    return withLoading(async () => {
      const response = await metaApi.getInsights(accountId, params);
      const ins = response.data || [];
      setInsights(ins);
      return ins;
    });
  }, []);

  // Fetch pages (for lead forms)
  const fetchPages = useCallback(async () => {
    return withLoading(async () => {
      const response = await metaApi.getPages();
      const pgs = response.data?.pages || [];
      setPages(pgs);
      return pgs;
    });
  }, []);

  // Fetch lead forms for a page
  const fetchLeadForms = useCallback(async (pageId) => {
    return withLoading(async () => {
      const response = await metaApi.getLeadForms(pageId);
      const forms = response.data?.forms || [];
      setLeadForms(forms);
      return forms;
    });
  }, []);

  // Fetch leads for a form (paginated — returns { leads, total, page, limit, totalPages })
  const fetchFormLeads = useCallback(async (formId, params = {}) => {
    return withLoading(async () => {
      const response = await metaApi.getFormLeads(formId, params);
      return response.data || { leads: [], total: 0, page: 1, limit: 0, totalPages: 0 };
    });
  }, []);

  // Fetch all leads (paginated — returns { leads, total, page, limit, totalPages })
  const fetchAllLeads = useCallback(async (params = {}) => {
    return withLoading(async () => {
      const response = await metaApi.getAllLeads(params);
      return response.data || { leads: [], total: 0, page: 1, limit: 0, totalPages: 0 };
    });
  }, []);

  // Fetch leads stats
  const fetchLeadsStats = useCallback(async () => {
    return withLoading(async () => {
      const response = await metaApi.getLeadsStats();
      return response.data || null;
    });
  }, []);

  const fetchPageSubscriptions = useCallback(async () => {
    return withLoading(async () => {
      const response = await metaApi.getPageSubscriptions();
      return response.data?.subscriptions || [];
    });
  }, []);

  // Re-checks subscribed pages directly against Meta (ground truth) — same check
  // that runs daily via cron, exposed here for an on-demand "Verificar Agora" button.
  const verifyPageSubscriptions = useCallback(async () => {
    return withLoading(async () => {
      const response = await metaApi.verifyPageSubscriptions();
      return response.data || null;
    });
  }, []);

  const syncPages = useCallback(async () => {
    return withLoading(async () => {
      const response = await metaApi.syncPages();
      toast.success(`${response.data?.count || 0} páginas sincronizadas!`);
      return response.data?.pages || [];
    });
  }, []);

  const subscribePage = useCallback(async (pageId) => {
    return withLoading(async () => {
      const response = await metaApi.subscribePage(pageId);
      if (response.data?.success) {
        toast.success('Página inscrita para receber leads!');
      } else {
        toast.error('Falha ao inscrever página');
      }
      return response.data;
    });
  }, []);

  const unsubscribePage = useCallback(async (pageId) => {
    return withLoading(async () => {
      await metaApi.unsubscribePage(pageId);
      toast.success('Página desinscrita');
    });
  }, []);

  // Save the external endpoint that receives a POST for every new lead on this page
  const updatePageWebhook = useCallback(async (pageId, data) => {
    return withLoading(async () => {
      const response = await metaApi.updatePageWebhook(pageId, data);
      toast.success('Webhook salvo!');
      return response.data;
    });
  }, []);

  // Fires a test payload at the configured endpoint, returns the real outcome
  const testPageWebhook = useCallback(async (pageId) => {
    const response = await metaApi.testPageWebhook(pageId);
    return response.data;
  }, []);

  return {
    // State
    accounts,
    campaigns,
    insights,
    pages,
    leadForms,
    loading,
    error,
    // Actions
    connectMeta,
    fetchAccounts,
    syncAccounts,
    fetchCampaigns,
    fetchInsights,
    fetchPages,
    fetchLeadForms,
    fetchFormLeads,
    fetchAllLeads,
    fetchLeadsStats,
    fetchPageSubscriptions,
    verifyPageSubscriptions,
    syncPages,
    subscribePage,
    unsubscribePage,
    updatePageWebhook,
    testPageWebhook,
  };
};

export default useMeta;
